import type Luminary from '@lumen5/luminary';
import type { Scene } from '@lumen5/luminary';
import { VideoData } from '@lumen5/luminary';

import { showsContentBeneathGraphic } from 'js/src/agent/html-design/layeredGraphic';
import {
    captureSceneScreenshot,
    SCREENSHOT_MAX_EDGE,
} from 'js/src/agent/rendering/sceneScreenshot';
import { TOOL_NAMES } from 'js/src/agent/toolNames';
import { sceneDisplayId } from 'js/src/agent/tools/scene-addressing';
import { checkpointVideoOperation } from 'js/src/data/video-data';
import type { AppDispatch, GetRootState, RootState } from 'js/src/libs/RootState';
import { isVoiceDrivenSceneGroup } from 'js/src/luminary';
import { addHtmlAssetToSceneOperation } from 'js/src/luminary/html-asset-operations';
import { makeOperationsCallable } from 'js/src/luminary/luminary-hooks';
import { isSceneAnOverlay } from 'js/src/luminary/luminary-selectors';

import { detectSpeakerHeads, type FrameBox } from './speakerFaces';

/** What a graphic subagent is started with, besides its brief. */
export interface GraphicRunSetup {
    /** Where the speaker's head is, or null when detection couldn't say. */
    heads: FrameBox[] | null;
    /** A still of the scene as it is now, or null when it couldn't be rendered. */
    still: string | null;
    /** The scene had no graphic, so one was added for this run. */
    addedGraphic: boolean;
}

/** Why a graphic can't be layered over the scene, or null when it can. */
export function graphicSceneProblem(
    luminary: Luminary,
    state: RootState,
    scene: Scene,
): string | null {
    const shortId = sceneDisplayId(luminary, scene.id);
    // A base scene spans whatever its group's overlays leave uncovered and is re-split as they
    // change, so a graphic on it would have no stable span to be timed against.
    if (
        scene.sceneGroup &&
        isVoiceDrivenSceneGroup(scene.sceneGroup) &&
        !isSceneAnOverlay(state, scene)
    ) {
        return (
            `scene ${shortId} is the base scene of a voice-driven scene group, whose span changes as ` +
            `overlays come and go. Give the graphic its own span first: ${TOOL_NAMES.addVoiceoverScene} ` +
            "with this scene's id as the scene_design_id copies it as an overlay over the words you " +
            'name; then start the graphic on that overlay.'
        );
    }
    if (!showsContentBeneathGraphic(scene)) {
        return `scene ${shortId} shows nothing for a graphic to be layered over. A graphic that fills the frame is a motion graphics scene, not a \`graphic\` subagent's.`;
    }
    return null;
}

/**
 * Readies the scene for its designer: gives it an empty graphic if it has none, and gathers where
 * the speaker is and a still of the scene beneath, both best effort.
 */
export async function prepareGraphicRun(
    luminary: Luminary,
    dispatch: AppDispatch,
    getState: GetRootState,
    scene: Scene,
): Promise<GraphicRunSetup | { error: string }> {
    const addedGraphic = scene.getAssetsByType(VideoData.HTML_ASSET_TYPE).length === 0;
    if (addedGraphic) {
        const { addHtmlAssetToScene } = makeOperationsCallable(
            { addHtmlAssetToSceneOperation },
            luminary,
            dispatch,
            getState,
        );
        if (!addHtmlAssetToScene(scene.id, '')) {
            return {
                error: `couldn't add a graphic to scene ${sceneDisplayId(luminary, scene.id)}.`,
            };
        }
    }
    const [heads, still] = await Promise.all([
        detectSpeakerHeads(luminary, dispatch, getState, scene),
        captureSceneScreenshot(luminary, scene, SCREENSHOT_MAX_EDGE.high).catch(() => null),
    ]);
    return { heads, still, addedGraphic };
}

/**
 * Takes back a graphic added for a run that never designed it. An empty graphic reads as a scene
 * awaiting a prompt, hiding what the scene shows, and only a graphic subagent can author it.
 */
export function removeUndesignedGraphic(
    luminary: Luminary,
    dispatch: AppDispatch,
    getState: GetRootState,
    scene: Scene,
    parentActionGroup: string | undefined,
): void {
    const [graphic] = scene.getAssetsByType(VideoData.HTML_ASSET_TYPE);
    if (!graphic) {
        return;
    }
    luminary.batchRun(() => {
        graphic.getParent()?.removeChild(graphic);
        graphic.destroy();
    });
    const { checkpointVideo } = makeOperationsCallable(
        { checkpointVideoOperation },
        luminary,
        dispatch,
        getState,
    );
    checkpointVideo({ systemTriggered: false, parentActionGroup });
}
